import React from 'react';
import ChecklistElement from './ChecklistElementModel';
import EditorType from '../../../Enums/EditorType';
import type { JSX } from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import type { SelectChangeEvent } from '@mui/material/Select';
import Select from '@mui/material/Select';
import type User from '../../UserModel';
import type UserDepartmentLink from '../../UserDepartmentLinkModel';
import type Department from '../../DepartmentModel';
import type DepartmentLink from '../../DepartmentLinkModel';
import { MenuItem } from '@mui/material';
import { removeDuplicates, removeNulls } from 'shouldve-been-js';

class DepartmentChecklistElement extends ChecklistElement {

    private _dependentElementId: string | null;

    public constructor(elementId: string, name: string, itemNumber: string | null, sortNumber: number, dependentElementId: string | null) {

        super(elementId, name, itemNumber, sortNumber, EditorType.DEPARTMENT);
        this._dependentElementId = dependentElementId;
    }

    public get dependentElementId(): string | null {
        return this._dependentElementId;
    }

    public set dependentElementId(value: string | null) {
        this._dependentElementId = value;
    }

    public buildFormElement(value: string, onChange: (value: string) => Promise<void>, departments: Department[], userDepartmentLinks: UserDepartmentLink[], dependentUser: User | null, screenWidth: number): JSX.Element {

        let currentDepartments: (Department | null)[] = departments;

        if (this.dependentElementId !== null) {

            const currentUserDepartmentLinks: DepartmentLink[] = userDepartmentLinks.find((userDepartmentlink: UserDepartmentLink) => userDepartmentlink.userId === dependentUser?.userId)?.departmentLinks ?? [];
            const currentRoleDepartmentLinks: DepartmentLink[] = userDepartmentLinks.find((userDepartmentlink: UserDepartmentLink) => userDepartmentlink.userId === dependentUser?.roleId)?.departmentLinks ?? [];

            const filteredUserDepartmentLinks: DepartmentLink[] = currentUserDepartmentLinks.filter((departmentLink: DepartmentLink) => departmentLink.status === '-');
            const filteredRoleDepartmentLinks: DepartmentLink[] = currentRoleDepartmentLinks.filter((departmentLink: DepartmentLink) => departmentLink.status === '-');

            const userDepartmentIds: string[] = filteredUserDepartmentLinks.map((departmentLink: DepartmentLink) => departmentLink.departmentId);
            const roleDepartmentIds: string[] = filteredRoleDepartmentLinks.map((departmentLink: DepartmentLink) => departmentLink.departmentId);

            const uniqueDepartmentIds: string[] = removeDuplicates(userDepartmentIds.concat(roleDepartmentIds));

            currentDepartments = uniqueDepartmentIds.map((departmentId: string) => departments.find((department: Department) => department.departmentId === departmentId) ?? null);
        }

        const filteredDepartments: Department[] = removeNulls(currentDepartments).filter((department: Department) => department.status === '-');

        const sortedDepartments: Department[] = [...filteredDepartments].sort((a: Department, b: Department) => a.name.localeCompare(b.name));

        const departmentMenuItems: JSX.Element[] = sortedDepartments.map((department: Department) => {

            return (
                <MenuItem
                    key={department.departmentId}
                    value={department.departmentId}
                >
                    {department.name}
                </MenuItem>
            );
        });

        return (
            <React.Fragment key={this.elementId}>
                <FormControl 
                    size='small'
                    style={{
                        width: Math.min(screenWidth * 0.8, 500),
                    }}
                >
                    <InputLabel>{this.name}</InputLabel>
                    <Select
                        value={value}
                        onChange={async (e: SelectChangeEvent): Promise<void> => onChange((e.target as HTMLInputElement).value)}
                        size='small'
                        variant='outlined'
                        label={this.name}
                    >
                        <MenuItem 
                            key='__empty__'
                            value=''
                        >
                            &nbsp;
                        </MenuItem> 
                        {value !== '' && !filteredDepartments.map((department: Department) => department.departmentId).includes(value) && //allows removed options to still be there until changed
                            <MenuItem 
                                key='__current__'
                                value={value}
                                disabled={true}
                            >
                                {value}
                            </MenuItem>
                        }
                        {departmentMenuItems}
                    </Select>
                </FormControl>
                <br />
                <br />
            </React.Fragment>
        );
    }
}

export default DepartmentChecklistElement;